/**
 * CategoryTree - Collapsible tree component for categories (WP-CAT-11, WP-CAT-13)
 *
 * Renders parent categories with expand/collapse and recursively renders children.
 *
 * Features:
 * - Collapsible sections with chevron indicator
 * - Recursive child rendering
 * - Counter showing enabled/total per node
 * - Keyboard navigation support
 * - SVG icons via CategoryIcon component
 */
import { useState, useCallback, useMemo } from 'react';
import { cn } from '@/utils/cn';
import CategoryRow from './CategoryRow';
import CategoryEnabledCounter from './CategoryEnabledCounter';
import { CategoryIcon } from '@/components/categories';
import type { CategoryWithPreference, CategoryType } from '@/types/category';

export interface CategoryTreeProps {
  /** All categories to display in tree */
  categories: CategoryWithPreference[];
  /** Category type to filter by (income, expense, transfer) */
  type: CategoryType;
  /** Callback when a category toggle is clicked */
  onToggle: (categoryId: string, isEnabled: boolean) => void;
  /** Set of category IDs currently being updated */
  updatingIds?: Set<string>;
  /** Whether the section is initially expanded */
  defaultExpanded?: boolean;
}

// Display labels for category types
const TYPE_LABELS: Record<CategoryType, string> = {
  income: 'Income',
  expense: 'Expenses',
  transfer: 'Transfers',
};

// Icons for category types
const TYPE_ICONS: Record<CategoryType, React.ReactNode> = {
  income: (
    <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  ),
  expense: (
    <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
    </svg>
  ),
  transfer: (
    <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
    </svg>
  ),
};

/**
 * Build a tree structure from flat category list.
 * Groups categories by parentId.
 */
function buildCategoryTree(categories: CategoryWithPreference[]): Map<string | undefined, CategoryWithPreference[]> {
  const tree = new Map<string | undefined, CategoryWithPreference[]>();

  // Group categories by parentId
  for (const cat of categories) {
    const parentKey = cat.parentId;
    if (!tree.has(parentKey)) {
      tree.set(parentKey, []);
    }
    tree.get(parentKey)!.push(cat);
  }

  // Sort each group by displayOrder
  for (const [, cats] of tree) {
    cats.sort((a, b) => a.displayOrder - b.displayOrder);
  }

  return tree;
}

/**
 * Recursive component for rendering a category node and its children.
 */
function CategoryNode({
  category,
  childrenMap,
  onToggle,
  updatingIds,
  depth = 0,
}: {
  category: CategoryWithPreference;
  childrenMap: Map<string | undefined, CategoryWithPreference[]>;
  onToggle: (categoryId: string, isEnabled: boolean) => void;
  updatingIds: Set<string>;
  depth: number;
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const children = childrenMap.get(category.id) || [];
  const hasChildren = children.length > 0;

  return (
    <div>
      {hasChildren ? (
        // Parent with children - show expand/collapse
        <div>
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className={cn(
              'flex items-center gap-2 w-full text-left py-2 px-2 hover:bg-gray-50 rounded-md transition-colors',
              !category.isEnabled && 'opacity-60'
            )}
            aria-expanded={isExpanded}
            style={{ marginLeft: depth > 0 ? `${depth * 1.5}rem` : undefined }}
          >
            {/* Chevron */}
            <svg
              className={cn(
                'w-4 h-4 text-gray-400 transition-transform flex-shrink-0',
                isExpanded && 'rotate-90'
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>

            {/* Icon */}
            <span className="flex-shrink-0" aria-hidden="true">
              <CategoryIcon
                name={category.icon}
                size="md"
                color={category.color}
                className="text-gray-600"
              />
            </span>

            {/* Name and badge */}
            <span className="flex-1 text-sm font-medium text-gray-900">{category.name}</span>

            {category.isSystem && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 flex-shrink-0">
                System
              </span>
            )}
          </button>

          {/* Children */}
          {isExpanded && (
            <div role="group" aria-label={`${category.name} subcategories`}>
              {children.map((child) => (
                <CategoryNode
                  key={child.id}
                  category={child}
                  childrenMap={childrenMap}
                  onToggle={onToggle}
                  updatingIds={updatingIds}
                  depth={depth + 1}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        // Leaf node - show toggle
        <CategoryRow
          category={category}
          onToggle={onToggle}
          isUpdating={updatingIds.has(category.id)}
          depth={depth}
        />
      )}
    </div>
  );
}

/**
 * CategoryTree component - collapsible tree for a category type section.
 *
 * Accessibility:
 * - Section header uses aria-expanded
 * - Tree structure uses role="group" for nesting
 * - Counter uses aria-live for updates
 */
export default function CategoryTree({
  categories,
  type,
  onToggle,
  updatingIds = new Set(),
  defaultExpanded = false,
}: CategoryTreeProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Filter categories by type
  const typeCategories = useMemo(
    () => categories.filter((cat) => cat.type === type),
    [categories, type]
  );

  // Build tree structure
  const childrenMap = useMemo(
    () => buildCategoryTree(typeCategories),
    [typeCategories]
  );

  // Get root categories (no parent)
  const rootCategories = useMemo(
    () => childrenMap.get(undefined) || [],
    [childrenMap]
  );

  // Calculate counts
  const enabledCount = useMemo(
    () => typeCategories.filter((cat) => cat.isEnabled).length,
    [typeCategories]
  );
  const totalCount = typeCategories.length;

  // Keyboard handler (defined before early return to satisfy rules-of-hooks)
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsExpanded(!isExpanded);
    }
  }, [isExpanded]);

  // Don't render if no categories of this type
  if (totalCount === 0) {
    return null;
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Section header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={handleKeyDown}
        className={cn(
          'flex items-center justify-between w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500'
        )}
        aria-expanded={isExpanded}
        aria-controls={`category-section-${type}`}
      >
        <div className="flex items-center gap-3">
          {/* Chevron */}
          <svg
            className={cn(
              'w-5 h-5 text-gray-500 transition-transform',
              isExpanded && 'rotate-90'
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>

          {/* Type icon */}
          {TYPE_ICONS[type]}

          {/* Type label */}
          <span className="font-medium text-gray-900">{TYPE_LABELS[type]}</span>
        </div>

        {/* Counter */}
        <CategoryEnabledCounter enabled={enabledCount} total={totalCount} />
      </button>

      {/* Category list */}
      {isExpanded && (
        <div
          id={`category-section-${type}`}
          className="px-2 py-2 divide-y divide-gray-100"
          role="list"
          aria-label={`${TYPE_LABELS[type]} categories`}
        >
          {rootCategories.map((category) => (
            <CategoryNode
              key={category.id}
              category={category}
              childrenMap={childrenMap}
              onToggle={onToggle}
              updatingIds={updatingIds}
              depth={0}
            />
          ))}
        </div>
      )}
    </div>
  );
}
